import {type TranslationObject} from '@colonist/i18n'
import {LobbyImage} from '@colonist/types'
import {createDivIn, createHeaderIn, createImgIn, createSliderIn, createSpanIn, setElementInnerText} from '../../ui_utils/DomUtils.ts'
import {createTooltipIn} from '../../ui_utils/TooltipUtils.ts'

export class UIRoomSliderController {
    private readonly container: HTMLDivElement
    protected sliderContainer: HTMLDivElement
    private sliderTrackContainer: HTMLDivElement
    protected slider: HTMLInputElement
    private sliderTrack: HTMLDivElement
    private sliderThumb: HTMLSpanElement
    private textDisplay: HTMLSpanElement

    protected headerContainer: HTMLDivElement
    private readonly title: TranslationObject

    private defaultValue: number
    private readonly min: number
    private max: number
    private readonly step: number
    private readonly onChange?: ((value: number) => void) | undefined
    private onInput: (value: number) => void

    private _isDisabled: boolean

    private sliderWarningImage: HTMLImageElement
    private sliderWarningThreshold: number
    private resizeObserver: ResizeObserver

    get isDisabled() {
        return this._isDisabled
    }

    set isDisabled(value: boolean) {
        this._isDisabled = value
        this.slider.disabled = value
        this.setSliderVisibility()
    }

    constructor(container: HTMLDivElement, title: TranslationObject, defaultValue: number, min: number, max: number, step: number = 1, onChange?: (value: number) => void, isDisabled: boolean = false) {
        this.container = container
        this.title = title
        this.defaultValue = defaultValue
        this.min = min
        this.max = max
        this.step = step
        this.onChange = onChange

        this.createContainer()
        this.createHeader()
        this.createSlider()

        this.isDisabled = isDisabled
    }

    private createContainer() {
        this.sliderContainer = createDivIn(this.container, 'room-settings-slider-container')
    }

    private createHeader() {
        this.headerContainer = createDivIn(this.sliderContainer, 'room-settings-slider-header')
        const titleContainer = createDivIn(this.headerContainer, 'room-settings-slider-title')
        createHeaderIn(titleContainer, this.title, '')
        this.sliderWarningImage = createImgIn(titleContainer, LobbyImage.DangerIcon, 'hidden')
        createTooltipIn(this.sliderWarningImage, {key: 'strings:roomPage.options.vpToWin.aboveRecommendedLimit'})
    }

    private createSlider() {
        this.sliderTrackContainer = createDivIn(this.sliderContainer, 'room-settings-slider-track-container')

        createSpanIn(this.sliderTrackContainer, 'room-settings-slider-label', {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.min}})

        const sliderWrapper = createDivIn(this.sliderTrackContainer, 'room-settings-slider-wrapper')
        this.slider = createSliderIn(sliderWrapper, 'room-settings-slider-input', '', this.min, this.max, this.step, this.defaultValue)
        this.sliderTrack = createDivIn(sliderWrapper, 'room-settings-slider-track')
        this.sliderThumb = createSpanIn(sliderWrapper, 'room-settings-slider-thumb', {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.defaultValue}})

        createSpanIn(this.sliderTrackContainer, 'room-settings-slider-label', {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.max}})

        this.textDisplay = createSpanIn(this.sliderContainer, 'room-settings-slider-text-display', {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.defaultValue}})

        this.slider.oninput = () => this.handleOnInput()
        this.slider.onchange = () => this.handleOnChange()

        this.resizeObserver = new ResizeObserver(() => this.updateSlider())
        this.resizeObserver.observe(this.slider)

        this.updateSlider()
    }

    private removeSlider() {
        this.resizeObserver.disconnect()
        this.sliderTrackContainer.remove()
        this.textDisplay.remove()
    }

    private setSliderVisibility() {
        this.sliderContainer.classList.toggle('view-only', this.isDisabled)
    }

    private updateSlider() {
        const currentValue = this.slider.valueAsNumber
        const percent = (currentValue - this.min) / (this.max - this.min)
        const sliderWidth = this.slider.offsetWidth

        const minThumbPadding = 4
        const thumbPosition = percent * sliderWidth
        const thumbDisplayedPosition = Math.max(minThumbPadding, Math.min(thumbPosition, sliderWidth - minThumbPadding))
        this.sliderThumb.style.left = `${thumbDisplayedPosition}px`

        setElementInnerText(this.sliderThumb, {key: 'strings:colonistCommon.utils.#buffer', options: {value: currentValue}})

        const isModified = currentValue != this.defaultValue
        this.sliderContainer.classList.toggle('modified', isModified)
        this.sliderThumb.classList.toggle('modified', isModified)
        this.sliderTrack.style.setProperty('--progress-percent', `${percent * 100}%`)

        this.sliderTrack.classList.toggle('warning', this.isAboveWarningThreshold())
        this.sliderWarningImage.classList.toggle('hidden', !this.isAboveWarningThreshold())

        setElementInnerText(this.textDisplay, {key: 'strings:colonistCommon.utils.#buffer', options: {value: currentValue}})
    }

    updateSliderConfig(defaultValue: number, max: number) {
        if(defaultValue == this.defaultValue && max == this.max) return

        this.defaultValue = defaultValue
        this.max = max

        this.removeSlider()
        this.createSlider()
    }

    setWarningThreshold(warningThreshold: number) {
        this.sliderWarningThreshold = warningThreshold
    }

    private isAboveWarningThreshold() {
        return this.slider.valueAsNumber > this.sliderWarningThreshold
    }

    private handleOnInput() {
        if(this.isDisabled) return
        if(this.onInput != undefined) {
            this.onInput(this.slider.valueAsNumber)
        }
        this.updateSlider()
    }

    private handleOnChange() {
        this.container.style.overflow = ''
        if(this.isDisabled) return
        if(this.onChange) this.onChange(this.slider.valueAsNumber)
    }

    setOnInput(onInput: (value: number) => void) {
        this.onInput = onInput
    }

    setValue(value: number, isDisabled: boolean = false) {
        this.slider.value = value.toString()
        this.isDisabled = isDisabled
        this.updateSlider()
    }
}
